import {PageTitle} from "../../../Components/Landmarks";
import React, {useEffect, useState} from "react";
import {orderManagementModel} from "./orderManagementFactory";
import {orderManagementApi} from "./orderManagementApi";
import {OrderManagementView} from "./OrderManagementView";
import {OrderManagementSearchFormView} from "./OrderManagementSearchFormView";
import {gridConstants, GridContainer, GridCell} from "../../../Components/Grid";
import {orderRequestApi} from "../OrderRequest/orderRequestApi";
import {createListFromArrayOfObjects} from "../../../Utilities/Types/listUtilities";
import {isNullOrUndefined} from "../../../Utilities/commonUtilities";
import {LayoutFullWidth} from "../../../Components/Layout";
import {libraryCatalogApi} from "../../Library/libraryCatalogApi";
import {ORDER_TYPE} from "../orderConstants";
import {SYSTEM_TYPES} from "../../Library/libraryConstants";
import { ButtonLinkStyle } from "../../../Components/Inputs";
import {allow, policyEvents} from "../../../Components/Authorize";
import {Order, OrderSearchCriteria} from "./orderManagement.types";
import type {HandleApiCall} from "../../../types";
import type {ListItem} from "../../../Utilities/Types/listUtilities";

interface OrderManagementContainerProps {
    districts: { id: string; districtName: string }[];
    handleApiCall: HandleApiCall;
    handleSetOrderSearchCriteria: (criteria: OrderSearchCriteria) => void;
    isLoading: boolean;
    orderSearchCriteria: OrderSearchCriteria;
    redirect: (path: string) => void;
}

export const OrderManagementContainer = ({
                                             districts,
                                             handleApiCall,
                                             handleSetOrderSearchCriteria,
                                             isLoading,
                                             orderSearchCriteria,
                                             redirect,
                                         }: OrderManagementContainerProps) => {

    const [orders, setOrders] = useState<Order[]>([]);
    const [shippingLocations, setShippingLocations] = useState<ListItem[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchDropdowns, setSearchDropdowns] = useState(null);
    const noResults = isNullOrUndefined(orders) || orders.length === 0;

    const [vendors, setVendors] = useState<{ vendorId: string; name: string }[]>();
    const vendorList = createListFromArrayOfObjects(vendors, "vendorId", "name");

    const canManageOrders = allow(policyEvents.MANAGE_ORDER);

    const buttonSharedSettings = {
        medium_columns: gridConstants.column.THREE,
        small_columns: gridConstants.column.SIX,
    };

    const handleCancelOrder = async (orderId: string) => {
        if (confirm(`Are you sure you wanted to cancel this order?`)) {
            handleApiCall(() => orderRequestApi.cancelOrder(orderId), () => {
                getOrders();
            });
        }
    }
    const handleExportOrdersClick = () => {
        if (noResults) {
            alert("Search criteria has no results. Adjust your search and try again.")
        } else {
            handleApiCall(() => orderManagementApi.exportOrdersReport(orderSearchCriteria));
        }
    }

    const loadOrderResults = (result: { data: Partial<Order>[]; totalRecords: number }) => {
        const orders = result.data.map(orderManagementModel);
        setOrders(orders);
        setTotalRecords(result.totalRecords);
    };

    const loadShippingLocationResult = (results: { shippingLocationId: string; name: string }[]) => {
        setShippingLocations(createListFromArrayOfObjects(results, `shippingLocationId`, `name`));
    }

    const getOrders = () => {
        if(orderSearchCriteria.orderType.value ===  ORDER_TYPE.All.toString())
            orderSearchCriteria.orderType.value = "";
        handleApiCall(() => orderManagementApi.getOrders({
            ...orderSearchCriteria,
        }), loadOrderResults);
    }

    const updateOrderInState = (orderId: string, notes: string) => {
        setOrders((currentOrders) =>
            currentOrders.map((order) =>
                order.orderId === orderId
                    ? { ...order, notes }
                    : order
            )
        );
    };

    const handleSaveUpdatedOrder = (updatedOrder: Order) => {
        updateOrderInState(updatedOrder.orderId, updatedOrder.notes ?? "");
        handleApiCall(
            () => orderManagementApi.updateOrderNotes(updatedOrder.orderId, updatedOrder.notes ?? ""),
            () => getOrders()
        );
    };

    useEffect(() => {
        const getVendors = () => {
            const st = orderSearchCriteria?.systemType ?? SYSTEM_TYPES.Textbooks;
            handleApiCall(() => libraryCatalogApi.getVendors(st), (results: { vendorId: string; name: string }[]) => setVendors(results));
        }
        if (!vendors)
            getVendors();
    }, [vendors, orderSearchCriteria?.systemType]);

    useEffect(() => {
        const getSearchDropdowns = () => {
            handleApiCall(() => orderManagementApi.getDropdownListsForOrders(), (results) => setSearchDropdowns(results));
        };
        getSearchDropdowns();
    }, []);

    useEffect(() => {
        if (isNullOrUndefined(districts) || districts.length === 0) return;
        const getShippingLocations = () => {
            handleApiCall(() => orderRequestApi.getShippingLocations(), loadShippingLocationResult);
        }
        if (!shippingLocations)
            getShippingLocations();
        if (orderSearchCriteria.loadSearchResults)
            getOrders();
    }, [districts, orderSearchCriteria]);

    if (!orderSearchCriteria) return null;

    return <>
        <PageTitle h1={`Order Management`}/>
        {shippingLocations &&
            <div className={`searchBox`}>
                <div className={`searchBox__title`}>Search for Orders</div>
                <OrderManagementSearchFormView
                    buttonSharedSettings={buttonSharedSettings}
                    districts={districts}
                    handleSetOrderSearchCriteria={handleSetOrderSearchCriteria}
                    isLoading={isLoading}
                    vendorList={vendorList}
                    orderSearchCriteria={orderSearchCriteria}
                    shippingLocations={shippingLocations}
                    searchDropdowns={searchDropdowns}
                />
            </div>
        }
        <LayoutFullWidth className={`catalog`}>
            <header>
                <h2>Search Results</h2>
            </header>
            <GridContainer isReverseOrder hasMarginX hasMarginY className={`text-right`}>
                <GridCell>
                    <ButtonLinkStyle
                        isCollapsed
                        label={`Export Orders`}
                        name="btnExportOrders"
                        onClick={handleExportOrdersClick}
                    />
                </GridCell>
            </GridContainer>
            <OrderManagementView
                canManageOrders={canManageOrders}
                handleCancelOrder={handleCancelOrder}
                handleSaveUpdatedOrder={handleSaveUpdatedOrder}
                handleSetOrderSearchCriteria={handleSetOrderSearchCriteria}
                orderSearchCriteria={orderSearchCriteria}
                orders={orders}
                redirect={redirect}
                totalRecords={totalRecords}
            />
        </LayoutFullWidth>
    </>;
};

